import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { SignJWT } from 'jose';

import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import { Dropdown } from '@components/Dropdown';
import Input from '@components/Input';
import Loading from '@components/Loading';
import Toggle from '@components/Toggle';

import { useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import PrivateSubmissionLayout from '@private-crm-app/components/PrivateSubmissionLayout/PrivateSubmissionLayout';
import { ELEVIO_PORTAL_URL, PRIVATE_APP_TOKEN } from '@private-crm-app/constants';
import {
  editEmailRequest,
  getAgencyFlags,
  getAuth0UserContact,
  getContactCustomFields,
  initiatePhoneChange,
  resetCitizenPassword,
  verifyEmailChange,
  verifyPhoneChange,
} from '@private-crm-app/requests/user';
import { useUserContext } from '@private-crm-app/user-context';
import { trackUpdateProfile, getCRMToken, getAgencyId } from '@private-crm-app/utils';

import ContactUtil from '@utils/ContactUtil';
import { joinFullName, splitFullName } from '@utils/NameUtil';
import { stateOptions } from '@utils/StateUtil';

import EmailEditModal from '../components/EmailEditModal/EmailEditModal';
import PhoneEditModal from '../components/PhoneEditModal/PhoneEditModal';
import { useProfileEditStatus } from '../hooks/useFetchEditStatus';

// Phone edit modal will be introduced separately
import './style.scss';

// SessionStorage key for success banner
const PROFILE_UPDATE_SUCCESS_KEY = 'profileUpdateSuccess';

export default function UserProfile() {
  const { user, updateUser, updateError, isLoading: isUserLoading, refreshUser } = useUserContext();
  const { auth0User, logout } = useAuthManagerContext();

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>(user?.email || '');
  const [phone, setPhone] = useState<string>(user?.cellPhone ?? '');
  const [address, setAddress] = useState<string>(user?.streetAddress ?? '');
  const [city, setCity] = useState<string>(user?.city ?? '');
  const [state, setState] = useState<string>(user?.state ?? '');
  const [stateSearchQuery, setStateSearchQuery] = useState<string>('');
  const [zipCode, setZipCode] = useState<string>(user?.zip ?? '');
  const [emailNotifications, setEmailNotifications] = useState<boolean>(
    user?.notificationPreference.includes(NotificationPreferenceType.EMAIL) && !!user?.email
  );
  const [phoneNotifications, setPhoneNotifications] = useState<boolean>(
    user?.notificationPreference.includes(NotificationPreferenceType.SMS) && !!user?.cellPhone
  );
  const [pushNotifications, setPushNotifications] = useState<boolean>(
    user?.notificationPreference.includes(NotificationPreferenceType.PUSH) ?? false
  );
  const [notifications, setNotifications] = useState<boolean>(
    user?.notificationPreference.includes(NotificationPreferenceType.EMAIL) ||
      user?.notificationPreference.includes(NotificationPreferenceType.SMS) ||
      user?.notificationPreference.includes(NotificationPreferenceType.PUSH)
  );
  const [isMobileAppEnabled, setIsMobileAppEnabled] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [changes, setChanges] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);

  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [emailUpdateError, setEmailUpdateError] = useState<string>('');
  const [isEmailUpdating, setIsEmailUpdating] = useState<boolean>(false);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState<boolean>(false);
  const [emailChangeVerificationSuccess, setEmailChangeVerificationSuccess] = useState<boolean>(false);
  const [emailChangeVerificationError, setEmailChangeVerificationError] = useState<string>('');
  const [newEmailAddress, setNewEmailAddress] = useState<string>('');
  const [isVerifyingEmail, setIsVerifyingEmail] = useState<boolean>(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>('');
  const [showPhoneModal, setShowPhoneModal] = useState<boolean>(false);
  const [phoneInitiateError, setPhoneInitiateError] = useState<string>('');
  const [phoneVerifyError, setPhoneVerifyError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isPhoneInitiating, setIsPhoneInitiating] = useState<boolean>(false);
  const [isPhoneVerifying, setIsPhoneVerifying] = useState<boolean>(false);
  const [phoneTransactionId, setPhoneTransactionId] = useState<string>('');
  const [otpExpiresAt, setOtpExpiresAt] = useState<string>('');
  const [overridePhone, setOverridePhone] = useState<string | null>(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<boolean>(false);
  const [resetPasswordError, setResetPasswordError] = useState<string>('');

  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Custom fields state
  const [contactCustomFields, setContactCustomFields] = useState<IContactCustomFieldResponse[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState(user?.contactCustomFieldValues || {});

  const { isEditAllowed } = useProfileEditStatus({ userId: user?.id });

  const contactUtil = new ContactUtil();

  // Fetch custom fields and agency flags on mount
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const fields = await getContactCustomFields();
        setContactCustomFields(fields);
      } catch {
        // Custom fields are optional for profile display
      }
    };

    const fetchAgencyFlags = async () => {
      const agencyId = getAgencyId();
      if (!agencyId) {
        return;
      }
      try {
        const flags = await getAgencyFlags(agencyId);
        setIsMobileAppEnabled(flags?.isMobileAppEnabled ?? false);
      } catch {
        // Flags are optional; push toggle stays hidden on error
      }
    };

    fetchCustomFields();
    fetchAgencyFlags();
  }, []);

  const handleEditPhone = useCallback(() => {
    if (!isEditAllowed) {
      return;
    }

    setShowPhoneModal(true);
    setPhoneInitiateError('');
    setPhoneVerifyError('');
    setPhoneTransactionId('');
  }, [isEditAllowed]);

  const handleResetPassword = useCallback(async () => {
    if (!isEditAllowed || !user?.email) {
      return;
    }
    setResetPasswordSuccess(false);
    setResetPasswordError('');
    try {
      await resetCitizenPassword(user.email);
      setResetPasswordError('');
      setResetPasswordSuccess(true);
    } catch (e) {
      setResetPasswordSuccess(false);
      setResetPasswordError('Failed to send the password reset email. Please try again.');
    }
  }, [isEditAllowed, user?.email]);

  const handleClosePhoneModal = useCallback(() => {
    setShowPhoneModal(false);
    setPhoneInitiateError('');
    setPhoneVerifyError('');
    setPhoneTransactionId('');
  }, []);

  // Sync custom field values when user changes
  useEffect(() => {
    if (user?.contactCustomFieldValues) {
      setCustomFieldValues(user.contactCustomFieldValues);
    }
  }, [user]);

  const getContactCustomFieldsByType = (contactType: ContactType) =>
    contactUtil.getContactCustomFieldsByType(contactType, contactCustomFields, user, user?.id);

  // Format phone number for display
  const formatPhoneForDisplay = useCallback((phone: string | null | undefined): string => {
    if (!phone) {
      return '';
    }

    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');

    // Handle different phone number lengths
    if (digits.length === 11 && digits.startsWith('1')) {
      // US number with country code: +1 (XXX) XXX-XXXX
      const areaCode = digits.slice(1, 4);
      const exchange = digits.slice(4, 7);
      const number = digits.slice(7);

      return `+1 (${areaCode}) ${exchange}-${number}`;
    } else if (digits.length === 10) {
      // US number without country code: (XXX) XXX-XXXX
      const areaCode = digits.slice(0, 3);
      const exchange = digits.slice(3, 6);
      const number = digits.slice(6);

      return `(${areaCode}) ${exchange}-${number}`;
    } else {
      // International or other format - just return with + if it starts with +
      return phone.startsWith('+') ? phone : `+${phone}`;
    }
  }, []);

  const isGoogleOAuthUser = useCallback(
    (): boolean => !!auth0User?.sub?.startsWith('google-oauth2|'),
    [auth0User?.sub]
  );

  const isAppleOAuthUser = useCallback(
    (): boolean => !!auth0User?.sub?.startsWith('apple|'),
    [auth0User?.sub]
  );

  const isSMSUser = useCallback((): boolean => !!auth0User?.sub?.startsWith('sms|'), [auth0User?.sub]);

  const PHONE_PATTERNS = {
    US_NUMBER: /^[2-9][0-9]{2}[2-9][0-9]{6}$/,
    INTERNATIONAL: /^[1-9][0-9]{10,11}$/,
  } as const;

  const PHONE_LENGTHS = {
    US_LOCAL: 10,
    US_WITH_CODE: 11,
    INTERNATIONAL_MIN: 11,
    INTERNATIONAL_MAX: 12,
  } as const;

  const extractDigits = useCallback((value: string): string => value.replace(/\D/g, ''), []);

  const isValidUSNumber = useCallback((digits: string): boolean => PHONE_PATTERNS.US_NUMBER.test(digits), []);

  const isValidE164Phone = useCallback(
    (value: string): boolean => {
      if (!value?.trim()) {
        return true;
      }

      const digits = extractDigits(value);

      if (digits.length === PHONE_LENGTHS.US_LOCAL) {
        return isValidUSNumber(digits);
      }

      if (digits.length === PHONE_LENGTHS.US_WITH_CODE && digits.startsWith('1')) {
        const withoutCountryCode = digits.slice(1);

        return isValidUSNumber(withoutCountryCode);
      }

      if (digits.length >= PHONE_LENGTHS.INTERNATIONAL_MIN && digits.length <= PHONE_LENGTHS.INTERNATIONAL_MAX) {
        return PHONE_PATTERNS.INTERNATIONAL.test(digits);
      }

      return false;
    },
    [extractDigits, isValidUSNumber]
  );

  const normalizePhoneNumber = useCallback(
    (value: string): string => {
      if (!value?.trim()) {
        return '';
      }

      if (value.startsWith('+')) {
        return value;
      }

      const digits = extractDigits(value);

      if (digits.length === PHONE_LENGTHS.US_LOCAL && isValidUSNumber(digits)) {
        return `+1${digits}`;
      }

      if (digits.length === PHONE_LENGTHS.US_WITH_CODE && digits.startsWith('1')) {
        const withoutCountryCode = digits.slice(1);
        if (isValidUSNumber(withoutCountryCode)) {
          return `+${digits}`;
        }
      }

      if (
        digits.length >= PHONE_LENGTHS.INTERNATIONAL_MIN &&
        digits.length <= PHONE_LENGTHS.INTERNATIONAL_MAX &&
        PHONE_PATTERNS.INTERNATIONAL.test(digits)
      ) {
        return `+${digits}`;
      }

      return value;
    },
    [extractDigits, isValidUSNumber]
  );

  const isValidEmail = useCallback((value: string): boolean => {
    if (!value || !value.trim()) {
      return true;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(value);
  }, []);

  const clearUpdateSuccess = useCallback(() => {
    setUpdateSuccess(false);
    // Remove from sessionStorage when banner is closed
    sessionStorage.removeItem(PROFILE_UPDATE_SUCCESS_KEY);
  }, []);

  const clearEmailChangeSuccess = useCallback(() => {
    setEmailChangeSuccess(false);
    setNewEmailAddress('');
  }, []);

  const clearResetPasswordSuccess = useCallback(() => {
    setResetPasswordSuccess(false);
  }, []);

  // Check for success state in sessionStorage on component mount
  useEffect(() => {
    const savedSuccessState = sessionStorage.getItem(PROFILE_UPDATE_SUCCESS_KEY);
    if (savedSuccessState === 'true') {
      setUpdateSuccess(true);
    }

    // Check for email verification success
    const emailVerificationSuccess =
      sessionStorage.getItem('emailVerificationSuccess') || localStorage.getItem('emailVerificationSuccess');
    if (emailVerificationSuccess === 'true') {
      setEmailChangeVerificationSuccess(true);
      setNewEmailAddress(''); // We don't have the new email address stored, so leave it empty
    }
  }, []);

  useEffect(() => {
    if (resetPasswordSuccess) {
      const timer = setTimeout(() => {
        clearResetPasswordSuccess();
      }, 5000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [resetPasswordSuccess, clearResetPasswordSuccess]);

  // User data synchronization - re-populate form fields when user data becomes available
  useEffect(() => {
    if (user) {
      const parsed = splitFullName(user.name || '');
      setFirstName(parsed.firstName || '');
      setLastName(parsed.lastName || '');
      setEmail(user.email || '');
      setPhone(user.cellPhone || '');
      setAddress(user.streetAddress || '');
      setCity(user.city || '');
      setState(user.state || '');
      setZipCode(user.zip || '');
      setEmailNotifications(user.notificationPreference.includes(NotificationPreferenceType.EMAIL) && !!user.email);
      setPhoneNotifications(user.notificationPreference.includes(NotificationPreferenceType.SMS) && !!user.cellPhone);
      setPushNotifications(user.notificationPreference.includes(NotificationPreferenceType.PUSH));
      setNotifications(
        user.notificationPreference.includes(NotificationPreferenceType.EMAIL) ||
          user.notificationPreference.includes(NotificationPreferenceType.SMS) ||
          user.notificationPreference.includes(NotificationPreferenceType.PUSH)
      );
      setIsInitializing(false);
    }
  }, [user]);

  // Fallback user fetching mechanism
  useEffect(() => {
    const fetchUserIfMissing = async () => {
      if (!user && !isUserLoading && auth0User?.sub && auth0User?.email) {
        try {
          // Call the same endpoint used in PrivateSubmissionListing
          const existingContactResponse = await getAuth0UserContact(auth0User.email, auth0User.sub);

          if (existingContactResponse?.individualContacts?.length > 0) {
            // Trigger user context refresh
            await refreshUser();
          } else {
            setIsInitializing(false);
          }
        } catch {
          setIsInitializing(false);
        }
      } else if (user || (!isUserLoading && !auth0User)) {
        setIsInitializing(false);
      }

      return; // Explicit return for async function
    };

    fetchUserIfMissing();
  }, [user, isUserLoading, auth0User, refreshUser]);

  // Handle success banner timeout
  useEffect(() => {
    if (updateSuccess) {
      const timer = setTimeout(() => {
        clearUpdateSuccess();
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [updateSuccess, clearUpdateSuccess]);

  useEffect(() => {
    if (emailChangeSuccess) {
      const timer = setTimeout(() => {
        clearEmailChangeSuccess();
      }, 5000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [emailChangeSuccess, clearEmailChangeSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      setError('');
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (updateError) {
      setUpdateSuccess(false);
      sessionStorage.removeItem(PROFILE_UPDATE_SUCCESS_KEY);
    }
  }, [updateError]);

  useEffect(() => {
    // const formatPhone = (phoneValue: string) => {
    //   if (!phoneValue) return '';
    //   let formatted = phoneValue.trim().replace(/[^0-9]/g, '');
    //   if (formatted.length === 10) {
    //     return `+1${formatted}`;
    //   } else if (formatted.length === 11) {
    //     return `+${formatted}`;
    //   }
    //   return formatted;
    // };

    // const formattedPhone = formatPhone(phone);
    // const originalPhone = user?.cellPhone || '';

    const currentEmailNotif = user?.notificationPreference.includes(NotificationPreferenceType.EMAIL);
    const currentPhoneNotif = user?.notificationPreference.includes(NotificationPreferenceType.SMS);
    const currentPushNotif = user?.notificationPreference.includes(NotificationPreferenceType.PUSH);

    const parsed = splitFullName(user?.name || '');
    const changes =
      firstName !== (parsed.firstName || '') ||
      lastName !== (parsed.lastName || '') ||
      (email || '') !== (user?.email || '') ||
      (phone || '') !== (user?.cellPhone || '') ||
      address !== (user?.streetAddress || '') ||
      city !== (user?.city || '') ||
      state !== (user?.state || '') ||
      zipCode !== (user?.zip || '') ||
      (notifications && emailNotifications) !== currentEmailNotif ||
      (notifications && phoneNotifications) !== currentPhoneNotif ||
      (notifications && pushNotifications) !== currentPushNotif;

    setChanges(changes);
  }, [
    firstName,
    lastName,
    email,
    phone,
    address,
    city,
    state,
    zipCode,
    notifications,
    emailNotifications,
    phoneNotifications,
    pushNotifications,
    user,
  ]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verifyToken = urlParams.get('verifyToken');
    const verificationSuccess = urlParams.get('verificationSuccess');
    const hasVerifiedEmail =
      sessionStorage.getItem('emailVerificationSuccess') === 'true' ||
      localStorage.getItem('emailVerificationSuccess') === 'true';

    // Early return if already verified or verification in progress
    if (hasVerifiedEmail || isVerifyingEmail || emailChangeVerificationSuccess) {
      return undefined;
    }

    // Handle verification success parameter
    if (verificationSuccess === 'true') {
      setEmailChangeVerificationSuccess(true);

      return undefined;
    }

    // Handle verification token
    if (verifyToken) {
      const timeoutId = setTimeout(() => {
        handleEmailChangeVerification(verifyToken);
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [window.location.search, isVerifyingEmail, emailChangeVerificationSuccess]);

  const handleEmailChangeVerification = async (token: string) => {
    // Prevent multiple simultaneous verifications
    if (isVerifyingEmail) {
      return;
    }

    // Check if already verified
    const hasVerifiedEmail =
      sessionStorage.getItem('emailVerificationSuccess') === 'true' ||
      localStorage.getItem('emailVerificationSuccess') === 'true';
    if (hasVerifiedEmail) {
      return;
    }

    setIsVerifyingEmail(true);

    try {
      const response = await verifyEmailChange(token);

      if (response.success) {
        await handleSuccessfulVerification(response.newEmail);
      }
    } catch (error) {
      await handleVerificationError(error);
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleSuccessfulVerification = async (newEmail: string) => {
    // Update state
    setNewEmailAddress(newEmail);
    setEmailChangeVerificationSuccess(true);
    setEmailChangeVerificationError('');

    // Store verification success
    sessionStorage.setItem('emailVerificationSuccess', 'true');
    localStorage.setItem('emailVerificationSuccess', 'true');
    // Mark this specifically as an email change verification (distinct from general email verification flows)
    sessionStorage.setItem('emailChangeVerification', 'true');
    localStorage.setItem('emailChangeVerification', 'true');

    // Schedule message hiding and redirect
    scheduleMessageHidingAndRedirect();
  };

  const handleVerificationError = async (error: any) => {
    const errorMessage =
      error?.response?.data?.message || error?.message || 'Email verification failed. Please try again.';

    setEmailChangeVerificationError(errorMessage);

    // Clear error after 8 seconds
    setTimeout(() => {
      setEmailChangeVerificationError('');
      localStorage.removeItem('emailVerificationSuccess');
      sessionStorage.removeItem('emailVerificationSuccess');
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }, 8000);

    // Clean URL
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
  };

  const scheduleMessageHidingAndRedirect = () => {
    // Hide message after 5 seconds
    setTimeout(() => {
      setEmailChangeVerificationSuccess(false);
      setNewEmailAddress('');
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      // Perform logout after 2 more seconds
      setTimeout(() => {
        try {
          localStorage.removeItem('private_app_token');
          localStorage.removeItem('emailVerificationSuccess');
          sessionStorage.removeItem('emailVerificationSuccess');
          // Clear the specific email change verification markers as well
          localStorage.removeItem('emailChangeVerification');
          sessionStorage.removeItem('emailChangeVerification');
          // Ensure LogoutHandler redirects to a route that triggers login (listing) and has CRM token
          const crmToken = getCRMToken();
          const loginRedirectUrl = `${process.env.APP_HOST_URL}/private-submission/listing${
            crmToken ? `?crm_token=${crmToken}` : ''
          }`;
          localStorage.setItem(ELEVIO_PORTAL_URL, loginRedirectUrl);
          // Explicitly trigger LoginChoice on landing if unauthenticated
          localStorage.setItem('trigger_login_choice', 'true');
          const currentPath = window.location.pathname;
          window.history.replaceState({}, document.title, currentPath);
          // Trigger Auth0 logout which will redirect to the logout handler (excluded from onboarding checks)
          // We do not await here since this runs inside a timeout; any errors are logged below
          void logout();
        } catch {
          // Logout is best-effort from this timeout; user can sign in again from listing.
        }
      }, 2000);
    }, 5000);
  };

  const onSave = async () => {
    if (notifications && !emailNotifications && !phoneNotifications && !pushNotifications) {
      setError('Please select at least one notification method.');

      return;
    }
    if (!firstName?.trim() || !lastName?.trim()) {
      setError('Please enter both first and last name.');

      return;
    }
    if (!isSMSUser()) {
      const trimmedPhone = (phone || '').trim();
      if (trimmedPhone && !isValidE164Phone(trimmedPhone)) {
        setPhoneError('Please enter a valid cell phone');

        return;
      }
    }

    if (isSMSUser()) {
      const trimmedEmail = (email || '').trim();
      if (trimmedEmail && !isValidEmail(trimmedEmail)) {
        setEmailError('Please enter a valid email address');

        return;
      }
    }
    setError(''); // Clear error if validation passes

    // Build payload without nullish email to satisfy OpenAPI validator
    const normalizedPhone = phone?.trim() ? normalizePhoneNumber(phone.trim()) : undefined;
    const contactRequest: IContactRequest = {
      name: joinFullName(firstName, lastName),
      // Do not include email if absent/null for SMS-only users
      cellPhone: normalizedPhone,
      streetAddress: address,
      city,
      state,
      zip: zipCode,
      notificationPreference: notifications
        ? ([
            emailNotifications ? NotificationPreferenceType.EMAIL : null,
            phoneNotifications ? NotificationPreferenceType.SMS : null,
            pushNotifications ? NotificationPreferenceType.PUSH : null,
          ].filter(Boolean) as NotificationPreferenceType[])
        : [],
      contactType: user?.contactType,
      isVendor: user?.isVendor,
      isGisPopulated: user?.isGisPopulated,
      isSourceAuth0: true,
    };

    if (typeof email === 'string' && email.trim().length > 0) {
      (contactRequest as any).email = email.trim();
    }

    try {
      await updateUser(user?.id, contactRequest);
      // Store success state in sessionStorage and set local state
      sessionStorage.setItem(PROFILE_UPDATE_SUCCESS_KEY, 'true');
      setUpdateSuccess(true);
      if (normalizedPhone) {
        setPhone(normalizedPhone);
      }
      trackUpdateProfile(joinFullName(firstName, lastName), user?.email);
    } catch {
      // Error handling is already done in the user context
    }

    return; // Explicit return for async function
  };

  const resetForm = async () => {
    const parsed = splitFullName(user?.name || '');
    setFirstName(parsed.firstName || '');
    setLastName(parsed.lastName || '');
    setEmail(user?.email || '');
    setPhone(user?.cellPhone ?? '');
    setAddress(user?.streetAddress ?? '');
    setCity(user?.city ?? '');
    setState(user?.state ?? '');
    setZipCode(user?.zip ?? '');
    setError('');
    setPhoneError('');
    setEmailError('');
    setNotifications(
      user?.notificationPreference.includes(NotificationPreferenceType.EMAIL) ||
        user?.notificationPreference.includes(NotificationPreferenceType.SMS) ||
        user?.notificationPreference.includes(NotificationPreferenceType.PUSH)
    );
    setEmailNotifications(user?.notificationPreference.includes(NotificationPreferenceType.EMAIL) && !!user?.email);
    setPhoneNotifications(user?.notificationPreference.includes(NotificationPreferenceType.SMS) && !!user?.cellPhone);
    setPushNotifications(user?.notificationPreference.includes(NotificationPreferenceType.PUSH) ?? false);

    return; // Explicit return for async function
  };

  // Render custom fields
  const renderCustomFields = () => {
    if (!contactCustomFields.length || !user?.contactType) {
      return null;
    }

    const activeCustomFields = getContactCustomFieldsByType(user.contactType);

    if (activeCustomFields.length === 0) {
      return null;
    }

    return (
      <div className="user-profile__container--personal-info-fields">
        <div className="input-wrapper">
          <h3 className="user-profile__container--custom-fields-title">Additional Information</h3>
        </div>
        {activeCustomFields.map((customField, index) => (
          <div key={index} className="user-profile__container--personal-info-fields">
            <div className="input-wrapper">
              <Input
                field={{
                  label: customField.label,
                  type: customField.type,
                  value: customFieldValues[customField.id] || '',
                  isRequired: false,
                  options: customField.options,
                }}
                width={5}
                name={`${customField.id}`}
                disabled={true} // Always disabled as requested
                className="user-profile__container--personal-info-field field-input"
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleEditEmail = (): void => {
    if (isGoogleOAuthUser() || isAppleOAuthUser() || !isEditAllowed) {
      return;
    }

    setShowEmailModal(true);
    setEmailUpdateError('');
    setEmailChangeSuccess(false);
  };

  const handleCloseEmailModal = (): void => {
    setShowEmailModal(false);
    setEmailUpdateError('');
    setEmailChangeSuccess(false);
  };

  const handleClearEmailError = (): void => {
    setEmailUpdateError('');
  };

  const handleSaveEmail = async (newEmail: string): Promise<void> => {
    setIsEmailUpdating(true);
    setEmailUpdateError('');

    try {
      await editEmailRequest(newEmail);

      setShowEmailModal(false);
      setNewEmailAddress(newEmail);
      setEmailChangeSuccess(true);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to request email change. Please try again.';

      setEmailUpdateError(errorMessage);
    } finally {
      setIsEmailUpdating(false);
    }
  };

  // const sendVerificationEmail = () => {
  //   const crmToken = getCRMToken();
  //   const email = auth0User?.email;
  //   const privateAppJWT =  auth0User?.privateAppJWT;

  //   if (!crmToken) {
  //     setError('Authentication error. Please try logging in again.');
  //     return;
  //   }

  //   if (!email) {
  //     setError('No email address found.');
  //     return;
  //   }

  //   if (!privateAppJWT) {
  //     setError('Authentication token not found. Please try logging in again.');
  //     return;
  //   }

  //   try {
  //     dispatch(sendVerificationEmailAction(email, crmToken, privateAppJWT));
  //   } catch (error) {
  //     console.error('Error sending verification email:', error);
  //     setError(error.message || 'Failed to send verification email.');
  //   }
  // };

  // Show loading spinner while initializing
  if (isInitializing || isUserLoading) {
    return <Loading show />;
  }

  const updateProfileDisabled =
    !isEditAllowed ||
    !changes ||
    (!isSMSUser() && Boolean(phone) && !isValidE164Phone(phone)) ||
    (isSMSUser() && Boolean(email) && !isValidEmail(email));

  return (
    <PrivateSubmissionLayout>
      <section className="user-profile">
        <header className="user-profile__header">
          <h1 className="user-profile__header--title">My Profile</h1>
          {!isEditAllowed && <div className="user-profile__alert">Please contact us to update your Profile.</div>}
        </header>

        {updateSuccess && (
          <div role="status" aria-live="polite" className="user-profile__alert user-profile__alert--success">
            <div className="alert-content">
              <i className="fa fa-check-circle"></i>
              <p>Your profile has been updated successfully.</p>
            </div>
            <button type="button" onClick={clearUpdateSuccess} className="close-btn" aria-label="Close">
              ×
            </button>
          </div>
        )}

        {emailChangeSuccess && (
          <div role="status" aria-live="polite" className="user-profile__alert user-profile__alert--success">
            <div className="alert-content">
              <i className="fa fa-check-circle"></i>
              <p>
                We've sent a verification email to <strong>{newEmailAddress}</strong>. Please check your inbox and click
                the verification link to complete the email change.
              </p>
            </div>
            <button type="button" onClick={clearEmailChangeSuccess} className="close-btn" aria-label="Close">
              ×
            </button>
          </div>
        )}

        {emailChangeVerificationSuccess && (
          <div>
            <div role="status" aria-live="polite" className="user-profile__alert user-profile__alert--success">
              <div className="alert-content">
                <i className="fa fa-check-circle"></i>
                <p>
                  Your email address has been successfully changed to <strong>{newEmailAddress}</strong>.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEmailChangeVerificationSuccess(false);
                  setNewEmailAddress('');
                }}
                className="close-btn"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div role="status" aria-live="polite" className="user-profile__alert user-profile__alert--success">
              <div className="alert-content">
                <i className="fa fa-info-circle"></i>
                <p>
                  To access your profile, submissions, and notifications, please log in using your new email address.
                  You will be automatically logged out in a few seconds for security.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEmailChangeVerificationSuccess(false);
                  setNewEmailAddress('');
                }}
                className="close-btn"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {emailChangeVerificationError && (
          <div role="alert" aria-live="assertive" className="user-profile__alert user-profile__alert--error">
            <div className="alert-content">
              <i className="fa fa-exclamation-triangle"></i>
              <p>{emailChangeVerificationError}</p>
            </div>
            <button
              type="button"
              onClick={() => setEmailChangeVerificationError('')}
              className="close-btn"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}

        {resetPasswordError && (
          <div role="alert" aria-live="assertive" className="user-profile__alert user-profile__alert--error">
            <div className="alert-content">
              <i className="fa fa-exclamation-triangle"></i>
              <p>{resetPasswordError}</p>
            </div>
            <button type="button" onClick={() => setResetPasswordError('')} className="close-btn" aria-label="Close">
              ×
            </button>
          </div>
        )}

        {resetPasswordSuccess && (
          <div role="status" aria-live="polite" className="user-profile__alert user-profile__alert--success">
            <div className="alert-content">
              <i className="fa fa-check-circle"></i>
              <p>Successfully sent password reset email.</p>
            </div>
            <button type="button" onClick={clearResetPasswordSuccess} className="close-btn" aria-label="Close">
              ×
            </button>
          </div>
        )}

        <div className="user-profile__container">
          <div className="user-profile__container--personal-info">
            {(error || updateError) && (
              <div role="alert" aria-live="assertive" className="error-message">
                {error || updateError}
              </div>
            )}
            <h2 className="user-profile__container--personal-info-title">My Info</h2>
            <div className="user-profile__container--personal-info-fields">
              <div className="input-wrapper">
                <Input
                  field={{
                    label: 'First Name',
                    type: CustomFieldType.TEXT,
                    isRequired: true,
                    value: firstName,
                  }}
                  width={5}
                  name="first-name"
                  className="user-profile__container--personal-info-field field-input"
                  onChange={(e) => setFirstName(e.currentTarget.value)}
                  disabled={!isEditAllowed}
                />
                <Input
                  field={{
                    label: 'Last Name',
                    type: CustomFieldType.TEXT,
                    isRequired: true,
                    value: lastName,
                  }}
                  width={5}
                  name="last-name"
                  className="user-profile__container--personal-info-field field-input"
                  onChange={(e) => setLastName(e.currentTarget.value)}
                  disabled={!isEditAllowed}
                />
              </div>
            </div>
            <div className="user-profile__container--personal-info-fields">
              <div className="input-wrapper">
                {isSMSUser() ? (
                  <>
                    <Input
                      field={{
                        label: 'Email',
                        type: CustomFieldType.TEXT,
                        isRequired: false,
                        value: email,
                      }}
                      width={5}
                      name="email"
                      className="user-profile__container--personal-info-field field-input"
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        setEmail(v);
                        if (!isValidEmail(v)) {
                          setEmailError('Please enter a valid email address');
                        } else {
                          setEmailError('');
                        }
                      }}
                      disabled={!isEditAllowed}
                      placeholder="Enter Email"
                    />
                  </>
                ) : (
                  <>
                    <Input
                      field={{
                        label: 'Cell Phone',
                        type: CustomFieldType.TEXT,
                        isRequired: false,
                        value: phone,
                      }}
                      width={5}
                      name="cell-phone"
                      className="user-profile__container--personal-info-field field-input"
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        setPhone(v);
                        if (!isValidE164Phone(v)) {
                          setPhoneError('Please enter a valid cell phone');
                        } else {
                          setPhoneError('');
                        }
                      }}
                      disabled={!isEditAllowed}
                      placeholder="Enter Cell Phone"
                    />
                  </>
                )}
              </div>
              {isSMSUser() && emailError && (
                <div role="alert" aria-live="polite" className="validation-error">
                  {emailError}
                </div>
              )}
              {!isSMSUser() && phoneError && (
                <div role="alert" aria-live="polite" className="validation-error">
                  {phoneError}
                </div>
              )}
            </div>
            <div className="user-profile__container--personal-info-fields">
              <div className="input-wrapper">
                <Input
                  field={{
                    label: 'Address',
                    type: CustomFieldType.TEXT,
                    isRequired: false,
                    value: address,
                  }}
                  width={5}
                  name="address"
                  className="user-profile__container--personal-info-field field-input"
                  onChange={(e) => setAddress(e.currentTarget.value)}
                  disabled={!isEditAllowed}
                />
                <Input
                  field={{
                    label: 'City',
                    type: CustomFieldType.TEXT,
                    isRequired: false,
                    value: city,
                  }}
                  width={5}
                  name="city"
                  className="user-profile__container--personal-info-field field-input"
                  onChange={(e) => setCity(e.currentTarget.value)}
                  disabled={!isEditAllowed}
                />
              </div>
            </div>
            <div className="user-profile__container--personal-info-fields">
              <div className="input-wrapper">
                <div className="field-dropdown">
                  <Dropdown
                    name="state"
                    id="state"
                    label="State"
                    value={state}
                    options={stateOptions(stateSearchQuery)}
                    onChange={(e) => setState(e.currentTarget.value)}
                    searchMode={true}
                    searchPlaceholder="Search state..."
                    type={DropdownType.BORDER}
                    className="user-profile__container--personal-info-field user-profile__container--personal-info-field--state"
                    shouldHighlightMatchingSubstring={false}
                    onLoadMoreData={({ query }: ILoadMoreParams) => setStateSearchQuery(query)}
                    disabled={!isEditAllowed}
                  />
                </div>
                <Input
                  field={{
                    label: 'Postal Code',
                    type: CustomFieldType.TEXT,
                    isRequired: false,
                    value: zipCode,
                  }}
                  width={5}
                  name="zip-code"
                  className="user-profile__container--personal-info-field field-input"
                  onChange={(e) => setZipCode(e.currentTarget.value)}
                  disabled={!isEditAllowed}
                />
              </div>
            </div>
            {renderCustomFields()}
          </div>
          <hr className="user-profile__container--account-info-divider" />
          <div className="user-profile__container--account-info">
            <h2 className="user-profile__container--account-info-title">My Account</h2>
            <div className="user-profile__container--personal-info-fields">
              <div className="account-info-wrapper">
                {!isSMSUser() ? (
                  isGoogleOAuthUser() ? (
                    <div className="user-profile__account-row">
                      <div className="user-profile__account-row-content">
                        <img src={'/assets/google.png'} alt="Google" className="user-profile__google-icon" />
                        <span className="user-profile__account-row-content-label">Managed through Google:</span>
                        <span className="user-profile__account-row-value">{user?.email || ''}</span>
                      </div>
                    </div>
                  ) : isAppleOAuthUser() ? (
                    <div className="user-profile__account-row">
                      <div className="user-profile__account-row-content">
                        <span className="user-profile__account-row-content-label">Managed through Apple:</span>
                        <span className="user-profile__account-row-value">Apple Private Relay</span>
                      </div>
                    </div>
                  ) : (
                    <div className="user-profile__account-row">
                      <div className="user-profile__account-row-content">
                        <span className="user-profile__account-row-content-label">Email:</span>
                        <span className="user-profile__account-row-value">{user?.email || ''}</span>
                      </div>
                      {user?.email && isEditAllowed && (
                        <div className="user-profile__account-row-content">
                          <button type="button" className="edit-link-button" onClick={handleResetPassword}>
                            Reset Password
                          </button>
                          <button type="button" className="edit-link-button" onClick={handleEditEmail}>
                            Edit Email
                          </button>
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div className="user-profile__account-row">
                    <div className="user-profile__account-row-content">
                      <span className="user-profile__account-row-content-label">Cell Phone:</span>
                      <span className="user-profile__account-row-value">
                        {formatPhoneForDisplay(overridePhone || user?.cellPhone)}
                      </span>
                    </div>
                    {user?.cellPhone && isEditAllowed && (
                      <div className="user-profile__account-row-content">
                        <button type="button" className="edit-link-button" onClick={handleEditPhone}>
                          Edit Cell Phone
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <hr className="user-profile__container--account-info-divider" />
          <div className="user-profile__container--notification-info">
            <h2 className="user-profile__container--notification-info-title">Notifications</h2>
            <Toggle
              bsStyle="primary"
              onToggle={() => setNotifications(!notifications)}
              toggleState={notifications}
              disabled={!isEditAllowed}
            >
              Receive Notification ({notifications ? 'On' : 'Off'})
            </Toggle>
            {notifications ? (
              <>
                <h3 className="user-profile__container--notification-info-subtitle">
                  Select your notification method for any updates to your requests
                </h3>

                <Stack direction="horizontal" gap={3} className="user-profile__container--notification-info-fields">
                  <Checkbox
                    id="email"
                    isChecked={emailNotifications}
                    disabled={!user.email || !isEditAllowed}
                    onChange={() => setEmailNotifications(!emailNotifications)}
                  >
                    Email
                  </Checkbox>
                  <Checkbox
                    id="phone"
                    isChecked={phoneNotifications}
                    disabled={!user?.cellPhone || !isEditAllowed}
                    onChange={() => setPhoneNotifications(!phoneNotifications)}
                  >
                    Phone
                  </Checkbox>
                  {isMobileAppEnabled && (
                    <Checkbox
                      id="push"
                      isChecked={pushNotifications}
                      disabled={!isEditAllowed}
                      onChange={() => setPushNotifications(!pushNotifications)}
                    >
                      Push Notifications
                    </Checkbox>
                  )}
                </Stack>
              </>
            ) : null}
          </div>
        </div>
        {isEditAllowed && (
          <footer className="user-profile__footer">
            <Button
              onClick={onSave}
              disabled={updateProfileDisabled}
              bgColor={updateProfileDisabled ? '#4b6b80' : undefined}
              color={updateProfileDisabled ? '#ffffff' : undefined}
            >
              Update Profile
            </Button>
            <Button onClick={resetForm} bsStyle={ButtonStyle.DEFAULT} disabled={!isEditAllowed}>
              Cancel
            </Button>
          </footer>
        )}
        <Link className="user-profile__submit" to={'/private-submission/create'}>
          <img src={'/assets/mail.svg'} alt="Mail" className="user-profile__mail-icon" />
          Submit Request
        </Link>
      </section>

      <EmailEditModal
        show={showEmailModal && !isGoogleOAuthUser() && !isAppleOAuthUser()}
        currentEmail={user?.email || ''}
        onClose={handleCloseEmailModal}
        onSave={handleSaveEmail}
        isLoading={isEmailUpdating}
        error={emailUpdateError}
        onErrorClear={handleClearEmailError}
      />
      <PhoneEditModal
        show={showPhoneModal}
        currentPhone={user?.cellPhone || ''}
        onClose={handleClosePhoneModal}
        onInitiate={async (phone: string) => {
          setIsPhoneInitiating(true);
          setPhoneInitiateError('');
          try {
            const res = await initiatePhoneChange(phone);
            setPhoneTransactionId(res.transactionId);
            setOtpExpiresAt(res.expiresAt);
            setNewPhoneNumber(phone);
            setOverridePhone(null);
          } catch (e: any) {
            setPhoneInitiateError(e?.response?.data?.message || 'Failed to send verification code');
          } finally {
            setIsPhoneInitiating(false);
          }
        }}
        onVerify={async (code: string) => {
          if (!phoneTransactionId) {
            setPhoneVerifyError('Start verification first');

            return;
          }
          setIsPhoneVerifying(true);
          setPhoneVerifyError('');
          try {
            await verifyPhoneChange(phoneTransactionId, code);
            setShowPhoneModal(false);
            if (newPhoneNumber) {
              setOverridePhone(newPhoneNumber);
              try {
                const secretKey = new TextEncoder().encode(process.env.JWT_SECRET || '');
                const token = await new SignJWT({
                  crmToken: getCRMToken() || '',
                  email: auth0User?.email,
                  auth0UserId: auth0User?.sub,
                  phone: newPhoneNumber,
                })
                  .setProtectedHeader({ alg: 'HS256' })
                  .sign(secretKey);
                localStorage.setItem(PRIVATE_APP_TOKEN, token);
              } catch {
                // Token refresh is optional; profile still updates after phone verify.
              }
            }
            await refreshUser();
          } catch (e: any) {
            setPhoneVerifyError(e?.response?.data?.message || 'Invalid verification code');
          } finally {
            if (isMountedRef.current) {
              setIsPhoneVerifying(false);
            }
          }
        }}
        isInitiating={isPhoneInitiating}
        isVerifying={isPhoneVerifying}
        initiateError={phoneInitiateError}
        verifyError={phoneVerifyError}
        transactionId={phoneTransactionId}
        expiresAt={otpExpiresAt}
      />
    </PrivateSubmissionLayout>
  );
}
